
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Loader from '@/components/Loader.vue'
import ContentChoice from '@/components/messages/ContentChoice.vue'
import ContentChoosed from '@/components/messages/ContentChoosed.vue'
import ContentFile from '@/components/messages/ContentFile.vue'
import ContentLocation from '@/components/messages/ContentLocation.vue'
import ContentTxt from '@/components/messages/ContentTxt.vue'
import ContentUrl from '@/components/messages/ContentUrl.vue'
import SurveyChoice from '@/components/messages/SurveyChoice.vue'
import TimeslotChoice from '@/components/messages/TimeslotChoice.vue'
import Timeslots from '@/components/messages/Timeslots.vue'
import Upload from '@/components/Upload.vue'
import WebSocketClient from '@/components/basic-native-rws'
import pageTitleNotification from '@/components/flash-title'

@Component({
  components: {
    Emoji: () => import(
      /* webpackChunkName: "component-emoji" */
      /* webpackPreload: -100 */
      '@/components/Emoji.vue'),
    ContentChoice,
    ContentChoosed,
    ContentFile,
    ContentLocation,
    ContentTxt,
    ContentUrl,
    Loader,
    SurveyChoice,
    TimeslotChoice,
    Timeslots,
    Upload
  }
})
export default class Chatbox extends Vue {
  @Prop() readonly channel!: Record<string, unknown>

  private initialScrolltop = 0
  private flashTitle = pageTitleNotification()

  get acceptedFileTypes (): string {
    return 'image/gif, image/jpeg, image/png, application/octet-stream, ' +
      'application/pdf, application/gzip, application/x-gzip, text/plain'
  }

  get fillColor (): string {
    return this.lightOrDark() === 'dark' ? 'white' : 'black'
  }

  get getBodyClasses (): string {
    const classes = ['chawgt-webchat-widget-wrapper chawgt-webchat-open']
    classes.push(this.lightOrDark())
    return classes.join(' ')
  }

  get isLoaderSeen (): boolean {
    return this.$store.getters['webchat/isLoaderSeen']
  }

  get isMobile (): boolean {
    return this.$store.getters.isMobile
  }

  get maxFileSize (): number | null {
    return this.$store.getters['webchat/uploadSize']
  }

  get messagesCount (): number {
    return this.$store.getters['webchat/messages'].length
  }

  get step (): string {
    return this.$store.getters['webchat/step']
  }

  get url (): string {
    return this.$store.getters['webchat/uploadUrl'] + '/api/uploads/'
  }

  get userMsg (): string {
    return this.$store.getters['webchat/userMsg']
  }

  set userMsg (value) {
    this.$store.commit('webchat/updateUserMsg', value)
  }

  addEmoji (emoji: any) {
    this.userMsg += emoji.data
    this.$store.commit('webchat/updateEmojiPickerShown', false)
  }

  afterEnter (): void {
    window.setTimeout(() => {
      this.scroll()
    }, 250)
  }

  autosize (e: { target: HTMLElement }): void {
    const el = e.target
    el.style.height = ''
    el.style.height = `${el.scrollHeight}px`
    this.scroll()
  }

  back (): void {
    this.$store.commit('webchat/updateStep', 'button')
  }

  close (): void {
    this.$store.commit('webchat/updateStep', 'button')
  }

  created () {
    Vue.prototype.$socket = new WebSocketClient()

    this.$store.dispatch({
      type: 'webchat/changeStep',
      step: this.channel.settings ? 'chatbox' : 'button'
    })

    window.addEventListener('blur', this.enablePush)
    window.addEventListener('focus', this.disablePush)
  }

  destroyed () {
    window.removeEventListener('blur', this.enablePush)
    window.removeEventListener('focus', this.disablePush)
  }

  disablePush (): void {
    this.$store.commit('webchat/updateIsAllowedToPush', false)
  }

  enablePush (): void {
    this.$store.commit('webchat/updateIsAllowedToPush', true)
    this.flashTitle.off()
  }

  lightOrDark (): string {
    if (this.$store.getters['webchat/hsp'] > 180) {
      return 'light'
    } else {
      return 'dark'
    }
  }

  postUploadHandler (params: Record<string, any>): void {
    this.$store.dispatch({
      type: 'webchat/sendMsg',
      content: params.metadata.url,
      richContent: {
        kind: 'file',
        file: {
          mimetype: params.metadata.mimetype,
          url: params.metadata.url,
          name: params.metadata.name
        }
      }
    })
    this.toggleUpload()
  }

  scroll (): void {
    const el = this.$refs.messages as HTMLElement
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }

  submitForm (): void {
    if (this.userMsg && this.userMsg.length > 0) {
      const userMsg = this.userMsg.trim()
      this.$store.dispatch({
        type: 'webchat/sendMsg',
        content: userMsg,
        richContent: userMsg
      })
      this.userMsg = ''
      this.$nextTick(() => {
        this.autosize({ target: this.$refs.messageField as HTMLElement })
      })
    }
  }

  toggleEmojiPicker (): void {
    this.$store.commit('webchat/updateEmojiPickerShown')
  }

  toggleUpload (): void {
    this.$store.commit('webchat/toggleUpload')
  }

  @Watch('isLoaderSeen')
  watchIsLoaderSeen () {
    this.$nextTick(() => {
      this.scroll()
    })
  }

  @Watch('messagesCount')
  watchMessagesCount () {
    this.scroll()
  }

  @Watch('step')
  watchStep (value: string) {
    if (value === 'chatbox') {
      this.$store.dispatch({
        type: 'webchat/initWebsocket'
      })
    }

    if (window.visualViewport !== undefined && this.isMobile) {
      if (value === 'chatbox') {
        this.initialScrolltop = document.documentElement.scrollTop
        document.body.classList.add('chawgt-webchat-open')
      } else {
        document.body.classList.remove('chawgt-webchat-open')
        window.scrollTo(0, this.initialScrolltop)
      }
    }
  }
}
